import { useMemo } from "react";
import { Close } from "@common/schemas/close.schema";
import {
    useOrganizationCloseStages,
    useSortedCloseStages,
} from "@api/organizationCloseStage.api";

export interface TaskMemberOption {
    id: number;
    name: string;
    sortName: string;
}

interface MemberWithUser {
    user: {
        id: number;
        name: string;
        preferredName?: string | null;
        legalName?: string | null;
    };
}

export const toTaskMemberOption = ({
    user,
}: MemberWithUser): TaskMemberOption => ({
    id: user.id,
    name: user.name,
    sortName: user.preferredName ?? user.legalName ?? "",
});

export function useTaskMemberLookups(members: TaskMemberOption[]) {
    const workspaceMemberIds = useMemo(
        () =>
            members
                .toSorted((a, b) => a.sortName.localeCompare(b.sortName))
                .map((member) => member.id),
        [members],
    );

    const workspaceMembersMap = useMemo(
        () => new Map(members.map((member) => [member.id, member.name])),
        [members],
    );

    return { workspaceMemberIds, workspaceMembersMap };
}

export function useCloseLookups(closes: Close[]) {
    const sortedCloses = useMemo(
        () => closes.toSorted((a, b) => a.name.localeCompare(b.name)),
        [closes],
    );

    const closeIds = useMemo(
        () => sortedCloses.map((close) => close.id),
        [sortedCloses],
    );

    const closesMap = useMemo(
        () => new Map(sortedCloses.map((close) => [close.id, close])),
        [sortedCloses],
    );

    return { closeIds, closesMap, hasCloses: closes.length > 0 };
}

export function useCloseStageLookups() {
    const sortedCloseStages = useSortedCloseStages();
    const { isLoading: areCloseStagesLoading } = useOrganizationCloseStages();

    const closeStageIds = useMemo(
        () => sortedCloseStages.map((stage) => stage.id),
        [sortedCloseStages],
    );

    const closeStagesMap = useMemo(
        () => new Map(sortedCloseStages.map((stage) => [stage.id, stage.name])),
        [sortedCloseStages],
    );

    return {
        closeStageIds,
        closeStagesMap,
        hasCloseStages: closeStageIds.length > 0,
        areCloseStagesLoading,
    };
}
